import { Box, CloseButton, Dialog, Image, PressableBox, Text } from '@opendoor/bricks-next';
import { QRCodeSVG } from 'qrcode.react';

const APP_DOWNLOAD_URL = 'https://opendoor.app.link/website';
const APPLE_STORE_LINK = 'https://apps.apple.com/us/app/opendoor-buy-sell-homes/id1143708068';
const GOOGLE_PLAY_LINK =
  'https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en';

const APP_STORE_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?service=cosmos';
const GOOGLE_PLAY_BADGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?service=cosmos';

const BADGE_HEIGHT = 44;
const BADGE_WIDTH_DESKTOP = 140;
const QR_SIZE = 96;
const PRESS_SCALE = 0.98;

const TITLE_DESKTOP = 'Get the Opendoor app to book your self-tour.';
const DESCRIPTION_DESKTOP =
  'Self-tours run from the Opendoor app. Once you’re in, unlock the door at the home with one tap.';
const TITLE_MOBILE = 'Tour any home, on your schedule';
const DESCRIPTION_MOBILE = 'Self-tours run from the Opendoor app. Install it to continue.';

export type SelfTourAppHandoffPlacement = 'footer' | 'lightbox' | 'main';

const buildAttributedUrl = (
  baseUrl: string,
  placement: SelfTourAppHandoffPlacement,
  addressToken?: string,
) => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'marketplace_pdp');
  url.searchParams.set('utm_medium', 'app_handoff_modal');
  url.searchParams.set('utm_content', placement);
  if (addressToken) {
    url.searchParams.set('utm_term', addressToken);
  }
  return url.toString();
};

export const SelfTourAppHandoffModal = ({
  isOpen,
  onClose,
  placement,
  addressToken,
}: {
  isOpen: boolean;
  onClose: () => void;
  placement: SelfTourAppHandoffPlacement;
  addressToken?: string;
}) => {
  const qrUrl = buildAttributedUrl(APP_DOWNLOAD_URL, placement, addressToken);
  const appleStoreUrl = buildAttributedUrl(APPLE_STORE_LINK, placement, addressToken);
  const googlePlayUrl = buildAttributedUrl(GOOGLE_PLAY_LINK, placement, addressToken);

  return (
    <Dialog
      isOpen={isOpen}
      dismissable
      analyticsName={`marketplace-self-tour-app-handoff-modal-${placement}`}
      trackImpression
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
    >
      {/* hideTitle/hideDescription wires the dialog's aria-labelledby/aria-describedby to the
          design copy while we render breakpoint-specific visible copy in the layout. */}
      <Dialog.Content
        title={TITLE_DESKTOP}
        description={DESCRIPTION_DESKTOP}
        hideTitle
        hideDescription
      >
        {/* Mobile-only close X — Bricks Dialog only renders its built-in CloseButton on desktop;
            the mobile sheet otherwise relies on swipe-to-dismiss. */}
        <Box
          flexDirection="row"
          justifyContent="flex-end"
          mb="$2x"
          $largerThanSM={{ display: 'none' }}
        >
          <CloseButton
            analyticsName={`close-self-tour-app-handoff-modal-${placement}`}
            onPress={onClose}
            size="large"
            variant="default"
          />
        </Box>
        <Box
          width="100%"
          flexDirection="column"
          gap="$8x"
          $largerThanSM={{
            width: 510,
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: '$8x',
          }}
        >
          <Box flex={1} flexDirection="column" gap="$4x" justifyContent="space-between">
            <Box flexDirection="column" gap="$4x">
              {/* Title — mobile copy on small screens */}
              <Text
                tag="span"
                typography="$subheaderMedium"
                color="$contentPrimary"
                $largerThanSM={{ display: 'none' }}
              >
                {TITLE_MOBILE}
              </Text>
              {/* Title — desktop copy on larger screens */}
              <Text
                tag="span"
                typography="$subheaderMedium"
                color="$contentPrimary"
                display="none"
                $largerThanSM={{ display: 'flex' }}
              >
                {TITLE_DESKTOP}
              </Text>
              {/* Description — mobile copy */}
              <Text
                tag="p"
                typography="$bodyMedium"
                color="$contentSecondary"
                $largerThanSM={{ display: 'none' }}
              >
                {DESCRIPTION_MOBILE}
              </Text>
              {/* Description — desktop copy */}
              <Text
                tag="p"
                typography="$bodyMedium"
                color="$contentSecondary"
                display="none"
                $largerThanSM={{ display: 'flex' }}
              >
                {DESCRIPTION_DESKTOP}
              </Text>
            </Box>
            <Box flexDirection="row" gap="$3x" mt="$2x" width="100%">
              <PressableBox
                href={appleStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                analyticsName={`self-tour-app-handoff-app-store-badge-${placement}`}
                aria-label="Download on the App Store"
                pressStyle={{ scale: PRESS_SCALE }}
                trackImpression
                flex={1}
                $largerThanSM={{ flex: 0 }}
              >
                <Image
                  source={{ uri: APP_STORE_BADGE_URL }}
                  alt="Download on the App Store"
                  width="100%"
                  height={BADGE_HEIGHT}
                  resizeMode="contain"
                  $largerThanSM={{ width: BADGE_WIDTH_DESKTOP }}
                />
              </PressableBox>
              <PressableBox
                href={googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                analyticsName={`self-tour-app-handoff-google-play-badge-${placement}`}
                aria-label="Get it on Google Play"
                pressStyle={{ scale: PRESS_SCALE }}
                trackImpression
                flex={1}
                $largerThanSM={{ flex: 0 }}
              >
                <Image
                  source={{ uri: GOOGLE_PLAY_BADGE_URL }}
                  alt="Get it on Google Play"
                  width="100%"
                  height={BADGE_HEIGHT}
                  resizeMode="contain"
                  $largerThanSM={{ width: BADGE_WIDTH_DESKTOP }}
                />
              </PressableBox>
            </Box>
          </Box>
          {/* Desktop-only QR section: gray rounded box wrapping a white rounded box around the QR. */}
          <Box
            display="none"
            $largerThanSM={{ display: 'flex' }}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="$5x"
            padding="$10x"
            backgroundColor="$backgroundSecondary"
            borderRadius="$6x"
            aria-label="QR code to download the Opendoor app"
            role="img"
          >
            <Box
              backgroundColor="$backgroundPrimary"
              borderRadius="$3x"
              padding="$3x"
              alignItems="center"
              justifyContent="center"
            >
              <QRCodeSVG value={qrUrl} size={QR_SIZE} />
            </Box>
            <Text
              tag="p"
              typography="$bodySmall"
              color="$contentPrimary"
              textAlign="center"
              maxWidth={120}
            >
              Scan with your phone camera
            </Text>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
